import * as t from 'io-ts';
import { Status, LanguageTranslationStatus } from '@/entities/checklist/constants';
import { checklistAuthorizedTo, id, links, meta } from '@/services/api';
import { withFallback } from 'io-ts-types/lib/withFallback';
import { DateFromUnixTime } from 'io-ts-types';

const categories = t.array(t.type({
  id: t.number,
  data: t.type({ title: t.string }),
}));

const status = t.union([
  t.literal(Status.Inactive),
  t.literal(Status.Active),
]);

const scheduleRuleCodec = t.type({
  start: t.union([DateFromUnixTime, t.null]),
  //can be 1, 2 but sometimes not number, but string from BE. look into rrule standard
  interval: t.union([t.number, t.string]),
  repeat: t.string,
});

const scheduleDataCodec = t.type({
  dueIn: t.number,
  rule: scheduleRuleCodec,
  separateTodoForAllUsers: t.number,
  supervisorsTarget: t.number,
  target: t.number,
  title: t.string,
});

const scheduleRecordCodec = t.type({
  id,
  data: scheduleDataCodec,
});

const scheduleList = t.array(scheduleRecordCodec);

export const data = t.type({
  title: t.string,
  description: withFallback(t.string, ''),
  categories,
  status,
  schedules: t.union([scheduleList, t.null, t.undefined]),
});

export const recordCodec = t.type({
  id,
  data,
  authorizedTo: checklistAuthorizedTo,
});

export const listCodec = t.type({
  data: t.array(recordCodec),
  meta,
  links,
});

export const detailCodec = t.type({
  data,
  authorizedTo: checklistAuthorizedTo,
});

export const formCodec = t.type({
  title: t.string,
  description: t.string,
  categories: t.array(id),
  status,
});

const checklistRestrictions = t.union([
  t.null,
  t.type({
    users: t.array(t.number),
    groups: t.array(t.number),
    entity_items: t.array(t.number),
    roles: t.array(t.number),
    administrators: t.boolean,
  }),
]);

const conductForm__params__responseSets = t.type({
  uuid: t.string,
  responses: t.array(t.type({
    uuid: t.string,
    title: t.string,
    color: t.string,
    score: t.union([t.number, t.null]),
    negative: t.boolean,
  })),
});

// Maybe we will be able to use this in other parts of the application
// that uses the form. Need to check in next tasks if we can add
// to those codecs as well
const conductForm = t.type({
  uuid: t.string,
  type: t.string,
  // @todo we are still missing the `items` keys
  params: t.type({
    format_version: t.number,
    response_sets: t.array(conductForm__params__responseSets),
  }),
});

const translationLanguageStatus = t.union([
  t.literal(LanguageTranslationStatus.pending),
  t.literal(LanguageTranslationStatus.inProgress),
  t.literal(LanguageTranslationStatus.completed),
  t.literal(LanguageTranslationStatus.failed),
]);

export const translationLanguageCodec = t.type({
  id,
  locale: t.string,
  aiEnabled: t.boolean,
  status: translationLanguageStatus,
});

const conductChecklist = t.type({
  checklist_title: t.string,
  checklist_title_translated: t.union([t.string, t.null, t.undefined]),
  checklist_description: t.union([t.string, t.null]),
  checklist_description_translated: t.union([t.string, t.null, t.undefined]),
  entity_type_of_response_migrated: t.boolean,
  autofill_entity: t.union([t.unknown, t.boolean]),
  blurFaces: t.union([t.boolean, t.undefined]),
  entity_items: t.union([t.array(t.unknown), t.record(t.string, t.string)]),
  categories: t.array(t.unknown),
  form: conductForm,
  schedules: t.array(t.number),
  restrictions: checklistRestrictions,
  report_template: t.union([t.null, t.number]),
  status,
  fullTranslationLanguages: withFallback(t.array(translationLanguageCodec), []),
  improveWriting: withFallback(t.union([t.boolean, t.undefined]), true),
});

const conductCategories = t.union([t.array(t.unknown), t.record(t.string, t.string)]);
const conductGroups = t.union([t.array(t.unknown), t.record(t.string, t.string)]);
const conductEntityItems = t.union([t.array(t.unknown), t.record(t.string, t.string)]);
const conductUsers = t.union([t.array(t.unknown), t.record(t.string, t.string)]);
const conductRoles = t.union([t.array(t.unknown), t.record(t.string, t.string)]);

export const checklistCodec = t.type({
  checklist: conductChecklist,
  groups: conductGroups,
  entity_items: conductEntityItems,
  users: conductUsers,
  roles: conductRoles,
  categories: conductCategories,
});

export const titleItemCodec = t.type({
  id: t.number,
  data: t.type({ title: t.string }),
});

export const translationLanguagesResponseCodec = t.type({
  data: t.array(translationLanguageCodec),
});

export const manualTranslationPayloadItemCodec = t.intersection([
  t.type({
    node_id: t.string,
    uuid: t.string,
  }),
  t.partial({
    title: t.string,
    htmlTitle: t.string,
  }),
]);

export const manualTranslationsPayloadCodec = t.intersection([
  t.type({
    checklistStatus: t.number,
    translations: t.array(manualTranslationPayloadItemCodec),
  }),
  t.partial({
    title: t.string,
    description: t.string,
  }),
]);
